import { createContext, useContext, useState, useCallback, useRef } from 'react';
import axiosClient from '../../api/axiosClient';
import { openContextualHelpChat } from '../../api/contextualHelpService';
import { stripDsmlMarkup } from 'utils/chatDisplay';

function sanitizeLoadedContent(content) {
  const stripped = stripDsmlMarkup(content ?? '').trim();
  return stripped || null;
}

export const CHAT_PENDING_CONVERSATION_ID = '__pending__';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState({});
  const [loadingConversations, setLoadingConversations] = useState(false);
  const sendMessageHandlerRef = useRef(null);

  const openDrawer = useCallback(() => setOpen(true), []);

  const registerSendMessageHandler = useCallback((handler) => {
    sendMessageHandlerRef.current = handler;
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    try {
      const { data } = await axiosClient.get(`/chat/conversations/${conversationId}/messages`);
      const rows = (data.messages ?? [])
        .filter((m) => m.role !== 'tool')
        .map((m) => {
          if (m.role !== 'assistant' || !m.content) return m;
          return { ...m, content: sanitizeLoadedContent(m.content) };
        });
      setMessages((prev) => ({ ...prev, [conversationId]: rows }));
    } catch {
      // silently ignore
    }
  }, []);

  const openContextualHelp = useCallback(
    async (prompt, locale = 'es', contextKey = null) => {
      openDrawer();
      const { conversationId, title } = await openContextualHelpChat(locale);
      setActiveConversationId(conversationId);
      setConversations((prev) => {
        const existing = prev.find((c) => c.id === conversationId);
        if (existing) {
          return prev.map((c) => (c.id === conversationId ? { ...c, title } : c));
        }
        return [{ id: conversationId, title, updatedAt: new Date().toISOString() }, ...prev];
      });
      if (!messages[conversationId]) {
        await loadMessages(conversationId);
      }
      if (sendMessageHandlerRef.current) {
        await sendMessageHandlerRef.current(prompt, conversationId);
      }
    },
    [openDrawer, messages, loadMessages]
  );

  const toggleDrawer = () => setOpen((o) => !o);

  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const { data } = await axiosClient.get('/chat/conversations');
      setConversations(data.conversations ?? []);
    } catch {
      // silently ignore
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  const selectConversation = useCallback(
    async (id) => {
      setActiveConversationId(id);
      if (id && !messages[id]) {
        await loadMessages(id);
      }
    },
    [messages, loadMessages]
  );

  const newConversation = useCallback(() => {
    setActiveConversationId(null);
  }, []);

  const deleteConversation = useCallback(
    async (id) => {
      try {
        await axiosClient.delete(`/chat/conversations/${id}`);
        setConversations((prev) => prev.filter((c) => c.id !== id));
        setMessages((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        if (activeConversationId === id) setActiveConversationId(null);
      } catch {
        // silently ignore
      }
    },
    [activeConversationId]
  );

  const appendMessage = useCallback((conversationId, message) => {
    setMessages((prev) => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] ?? []), message]
    }));
  }, []);

  const updateLastAssistantMessage = useCallback((conversationId, content) => {
    setMessages((prev) => {
      const msgs = prev[conversationId] ?? [];
      if (msgs.length === 0) return prev;
      const last = msgs[msgs.length - 1];
      if (last.role !== 'assistant') return prev;
      return {
        ...prev,
        [conversationId]: [...msgs.slice(0, -1), { ...last, content }]
      };
    });
  }, []);

  const updateLastAssistantReasoning = useCallback((conversationId, reasoning) => {
    setMessages((prev) => {
      const msgs = prev[conversationId] ?? [];
      if (msgs.length === 0) return prev;
      const last = msgs[msgs.length - 1];
      if (last.role !== 'assistant') return prev;
      return {
        ...prev,
        [conversationId]: [...msgs.slice(0, -1), { ...last, reasoning }]
      };
    });
  }, []);

  const updateMessageById = useCallback((conversationId, messageId, patch) => {
    setMessages((prev) => {
      const msgs = prev[conversationId] ?? [];
      const idx = msgs.findIndex((m) => m.id === messageId);
      if (idx < 0) return prev;
      const updated = { ...msgs[idx], ...patch };
      return {
        ...prev,
        [conversationId]: [...msgs.slice(0, idx), updated, ...msgs.slice(idx + 1)]
      };
    });
  }, []);

  const upsertConversationInList = useCallback((conv) => {
    setConversations((prev) => {
      const existing = prev.find((c) => c.id === conv.id);
      if (existing) return prev.map((c) => (c.id === conv.id ? { ...c, ...conv } : c));
      return [conv, ...prev];
    });
  }, []);

  const adoptPendingConversation = useCallback((conversationId) => {
    setMessages((prev) => {
      const pending = prev[CHAT_PENDING_CONVERSATION_ID];
      if (!pending?.length) return prev;
      const next = { ...prev };
      delete next[CHAT_PENDING_CONVERSATION_ID];
      next[conversationId] = pending;
      return next;
    });
  }, []);

  return (
    <ChatContext.Provider
      value={{
        open,
        openDrawer,
        toggleDrawer,
        registerSendMessageHandler,
        openContextualHelp,
        conversations,
        loadingConversations,
        loadConversations,
        activeConversationId,
        selectConversation,
        newConversation,
        deleteConversation,
        messages,
        loadMessages,
        appendMessage,
        updateLastAssistantMessage,
        updateLastAssistantReasoning,
        updateMessageById,
        upsertConversationInList,
        adoptPendingConversation,
        setActiveConversationId
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}
